import { RotateCcw, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import FactuurRij from '../onderdelen/FactuurRij'
import { FoutVak, Kiezer, Kop, Laden, Leeg } from '../onderdelen/ui'
import { api, Fout } from '../lib/api'
import { aantal } from '../lib/opmaak'
import { ga } from '../lib/router'
import { useStaat } from '../lib/staat'
import { zichtUit } from '../lib/bakken'
import type { Expense, Persoon, ZoekVraag } from '../lib/types'
import { overzichtenVoor, type Keuze } from '../lib/zicht.ts'

/* ===========================================================================
 *  Zoeken
 *
 *  Het scherm van Blue10: vrij zoeken, Overzicht, Bedrijf, Ligt bij, Debet
 *  of Credit, boekstuknummer, relatie en factuurnummer, en sorteren. Plus
 *  Inkoop of Verkoop, omdat die hier in één lijst staan.
 *
 *  Wat je invult blijft staan als je terugkomt van de resultaten: zoeken is
 *  vaak twee keer bijstellen.
 *
 *  Het Overzicht is sinds 23 okt 2026 dat van TW10 Online (zicht.ts): met
 *  Alles zien de keuzes van Blue10 (To-do, Te valideren, ..., Geen factuur,
 *  In dispuut, Alles), anders de eigen keuzes (To-do, Reeds vervallen + 7
 *  dagen, In dispuut, Eerder gezien, Mijn bedrijven, Te boeken, Alles wat ik
 *  zie). Welke facturen erbij horen, beslist de functie met dezelfde regel.
 *  Ligt bij staat er alleen met Alles zien, zoals daar. Begint op To-do,
 *  zoals het overzicht in TW10 Online.
 * =========================================================================== */

const LEEG: ZoekVraag = { overzicht: 'todo', richting: '', teken: '', sorteer: 'datum-af' }
let onthouden: ZoekVraag = { ...LEEG }

const SORTEER: { waarde: string; label: string }[] = [
  { waarde: 'datum-af', label: 'Factuurdatum, nieuwste eerst' },
  { waarde: 'datum-op', label: 'Factuurdatum, oudste eerst' },
  { waarde: 'bedrag-af', label: 'Bedrag, hoogste eerst' },
  { waarde: 'bedrag-op', label: 'Bedrag, laagste eerst' },
  { waarde: 'relatie', label: 'Relatie, A tot Z' },
  { waarde: 'verval', label: 'Vervaldatum, eerst vervallend' },
]

const RICHTING: { waarde: '' | 'inkoop' | 'verkoop'; label: string }[] = [
  { waarde: '', label: 'Inkoop en verkoop' },
  { waarde: 'inkoop', label: 'Inkoop' },
  { waarde: 'verkoop', label: 'Verkoop' },
]

type Open = null | 'overzicht' | 'bedrijf' | 'ligtBij' | 'sorteer' | 'richting'

export default function Zoeken() {
  const ik = useStaat((s) => s.ik)
  const z = ik ? zichtUit(ik, []) : null
  const overzichten: { waarde: Keuze; label: string }[] = z ? overzichtenVoor(z) : []
  const [v, setV] = useState<ZoekVraag>(onthouden)
  const [open, setOpen] = useState<Open>(null)
  const [personen, setPersonen] = useState<Persoon[] | null>(null)

  const zet = (deel: Partial<ZoekVraag>) => setV((oud) => { const nieuw = { ...oud, ...deel }; onthouden = nieuw; return nieuw })

  useEffect(() => {
    if (open === 'ligtBij' && !personen) void api.personen().then(setPersonen).catch(() => setPersonen([]))
  }, [open, personen])

  const bedrijfNaam = v.bedrijf ? ik?.bedrijven.find((b) => b.code === v.bedrijf)?.naam ?? v.bedrijf : 'Alle bedrijven'
  const ligtBijNaam = v.ligtBij ? personen?.find((p) => p.id === v.ligtBij)?.naam ?? 'Gekozen' : '(Leeg)'

  return (
    <div className="scherm zoeken">
      <Kop terugNaar="/" terugLabel="Dashboard" titel="Zoeken" />

      <form className="zoek-vorm" onSubmit={(e) => { e.preventDefault(); ga('/zoeken/resultaten') }}>
        <div className="zoek-vrij">
          <label className="zoekveld">
            <Search size={22} aria-hidden="true" />
            <input type="search" placeholder="Vrij zoeken" value={v.tekst ?? ''} onChange={(e) => zet({ tekst: e.target.value })} />
          </label>
          <button type="button" className="rond-knop klein-rond" aria-label="Alles leegmaken" onClick={() => { onthouden = { ...LEEG }; setV(onthouden) }}>
            <RotateCcw size={20} />
          </button>
        </div>

        <div className="zoek-raster">
          <Keuze label="Overzicht" waarde={overzichten.find((o) => o.waarde === v.overzicht)?.label ?? 'To-do'} opKlik={() => setOpen('overzicht')} />
          <Keuze label="Bedrijf" waarde={bedrijfNaam} opKlik={() => setOpen('bedrijf')} />
          {z?.alles && <Keuze label="Ligt bij" waarde={ligtBijNaam} opKlik={() => setOpen('ligtBij')} />}
          <div className="keuze">
            <div className="schuif klein-schuif" role="radiogroup" aria-label="Type">
              {(['debet', 'credit'] as const).map((t) => (
                <button type="button" key={t} role="radio" aria-checked={v.teken === t} className={v.teken === t ? 'aan' : ''}
                  onClick={() => zet({ teken: v.teken === t ? '' : t })}>
                  {t === 'debet' ? 'Debet' : 'Credit'}
                </button>
              ))}
            </div>
            <div className="onderschrift">Type</div>
          </div>
          <Keuze label="Soort" waarde={RICHTING.find((r) => r.waarde === (v.richting ?? ''))?.label ?? 'Inkoop en verkoop'} opKlik={() => setOpen('richting')} />
        </div>

        <Veld label="Boekstuknummer" waarde={v.boekstuk ?? ''} zet={(t) => zet({ boekstuk: t })} />
        <Veld label="Leverancier" waarde={v.relatie ?? ''} zet={(t) => zet({ relatie: t })} />
        <Veld label="Factuurnummer" waarde={v.factuurnummer ?? ''} zet={(t) => zet({ factuurnummer: t })} />

        <div className="midden-blok">
          <Keuze label="Sorteer op" waarde={SORTEER.find((s) => s.waarde === v.sorteer)?.label ?? 'Sorteer op'} opKlik={() => setOpen('sorteer')} />
        </div>

        <div className="voet-knop">
          <button type="submit" className="knop breed blauw groot">Zoeken</button>
        </div>
      </form>

      {open === 'overzicht' && (
        <Kiezer titel="Overzicht" terugLabel="Zoeken" opties={overzichten} gekozen={v.overzicht ?? 'todo'}
          kies={(w) => zet({ overzicht: w })} sluit={() => setOpen(null)} />
      )}
      {open === 'bedrijf' && (
        <Kiezer titel="Kies bedrijf" terugLabel="Zoeken" zoekbaar
          opties={[{ waarde: '', label: 'Alle bedrijven' }, ...(ik?.bedrijven ?? []).map((b) => ({ waarde: b.code, label: b.naam }))]}
          gekozen={v.bedrijf ?? ''} kies={(w) => zet({ bedrijf: w })} sluit={() => setOpen(null)} />
      )}
      {open === 'ligtBij' && (
        <Kiezer titel="Ligt bij" terugLabel="Zoeken" zoekbaar
          opties={[{ waarde: '', label: '(Leeg)' }, ...(personen ?? []).map((p) => ({ waarde: p.id, label: p.naam }))]}
          gekozen={v.ligtBij ?? ''} kies={(w) => zet({ ligtBij: w })} sluit={() => setOpen(null)}
          leegTekst={personen ? 'Niemand gevonden.' : 'Even ophalen…'} />
      )}
      {open === 'sorteer' && (
        <Kiezer titel="Sorteer op" terugLabel="Zoeken" opties={SORTEER} gekozen={v.sorteer ?? 'datum-af'}
          kies={(w) => zet({ sorteer: w })} sluit={() => setOpen(null)} />
      )}
      {open === 'richting' && (
        <Kiezer titel="Soort" terugLabel="Zoeken" opties={RICHTING} gekozen={v.richting ?? ''}
          kies={(w) => zet({ richting: w })} sluit={() => setOpen(null)} />
      )}
    </div>
  )
}

function Keuze({ label, waarde, opKlik }: { label: string; waarde: string; opKlik: () => void }) {
  return (
    <div className="keuze">
      <button type="button" className="knop lucht" onClick={opKlik}><span className="afkap">{waarde}</span></button>
      <div className="onderschrift">{label}</div>
    </div>
  )
}

function Veld({ label, waarde, zet }: { label: string; waarde: string; zet: (t: string) => void }) {
  return (
    <label className="lijnveld">
      <input placeholder={label} value={waarde} onChange={(e) => zet(e.target.value)} />
      <span className="klein zacht">{label}</span>
    </label>
  )
}

/* ------------------------------------------------------------------ */

export function Resultaten() {
  const [bonnen, setBonnen] = useState<Expense[]>([])
  const [totaal, setTotaal] = useState<number | null>(null)
  const [pagina, setPagina] = useState(0)
  const [laden, setLaden] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  async function haal(p: number) {
    setLaden(true)
    setFout(null)
    try {
      const uit = await api.zoek(onthouden, p)
      setBonnen((oud) => (p === 0 ? uit.bonnen : [...oud, ...uit.bonnen]))
      setTotaal(uit.totaal)
      setPagina(p)
    } catch (e) {
      setFout(e instanceof Fout ? e.message : String(e))
    } finally {
      setLaden(false)
    }
  }

  useEffect(() => { void haal(0) }, [])

  return (
    <div className="scherm">
      <Kop terugNaar="/zoeken" terugLabel="Zoeken" titel="Resultaten"
        ondertitel={totaal === null ? undefined : aantal(totaal, 'resultaat', 'resultaten')} />
      {fout && <FoutVak tekst={fout} opnieuw={() => void haal(pagina)} />}
      {totaal === 0 && <Leeg>Niets gevonden. Probeer minder filters.</Leeg>}
      <ul className="lijst">
        {bonnen.map((b) => <FactuurRij key={b.id} bon={b} lijst={bonnen} />)}
      </ul>
      {laden && <Laden />}
      {!laden && totaal !== null && bonnen.length < totaal && (
        <div className="voet-knop"><button className="knop breed lucht" onClick={() => void haal(pagina + 1)}>Meer laden</button></div>
      )}
    </div>
  )
}
